import {
  ActionIcon,
  Button,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, X as XIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from '../point-note-detail/page.module.css';

import { pointsApi } from '@fields/data-access';
import { FullMainHeight } from '@ui';

type LocalImage = {
  id: string;
  previewUrl: string;
  file: File;
};

export function PointNoteCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: '/map-note-create' });
  const search = useSearch({ from: '/_app/map-note-create' });
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedTitle = title.trim();
  const trimmedContent = content.trim();
  const titleError = submitAttempted && !trimmedTitle;
  const contentError = submitAttempted && !trimmedContent;
  const isFormValid = Boolean(trimmedTitle && trimmedContent);

  const createNoteMutation = useMutation({
    mutationFn: () =>
      pointsApi.createPointNote(search.pointId, {
        title: title.trim(),
        content: content.trim(),
        images: localImages.map((image) => image.file),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['points', search.pointId, 'notes'],
      });
      navigate({
        to: '/map-notes',
        search: {
          ilotId: search.ilotId,
          pointId: search.pointId,
          pointCode: search.pointCode,
          sampleId: search.sampleId,
          lng: search.lng,
          lat: search.lat,
          zoom: search.zoom,
        scope: search.scope,
        },
      });
    },
  });
  const canSave = isFormValid && !createNoteMutation.isPending;

  const handleCancel = () => {
    navigate({
      to: '/map-notes',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleAddImage = (e: { currentTarget: HTMLInputElement }) => {
    const files = Array.from(e.currentTarget.files ?? []);
    if (!files.length) return;
    const next = files.map((file) => ({
      id: `${file.name}-${file.lastModified}-${Math.random().toString(16).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setLocalImages((prev) => [...prev, ...next]);
    e.currentTarget.value = '';
  };

  const handleRemoveLocalImage = (id: string) => {
    setLocalImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) URL.revokeObjectURL(image.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  };

  useEffect(() => {
    return () => {
      localImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [localImages]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content className={classes.content}>
        <Stack gap={0} h="100%">
          <header className={classes.header}>
            <Text className={classes.headerTitle}>{t('sampling.notes.title')}</Text>
            <ActionIcon
              aria-label={t('sampling.notes.closeCreateAria')}
              color="dark"
              onClick={handleCancel}
              radius="xl"
              size={32}
              variant="light"
            >
              <XIcon size={18} />
            </ActionIcon>
          </header>

          <ScrollArea h="100%">
            <Stack className={classes.stack} gap="md">
              <Stack gap="sm">
                <TextInput
                  fw={700}
                  onChange={(e) => setTitle(e.currentTarget.value)}
                  placeholder={t('sampling.notes.titlePlaceholder')}
                  value={title}
                  variant="unstyled"
                />
                <span className={classes.greenLine} />
                {titleError ? (
                  <Text c="red" size="xs">
                    {t('sampling.notes.titleRequired')}
                  </Text>
                ) : null}

                <Textarea
                  autosize
                  className={classes.contentInput}
                  minRows={5}
                  onChange={(e) => setContent(e.currentTarget.value)}
                  placeholder={t('sampling.notes.commentPlaceholder')}
                  value={content}
                />
                {contentError ? (
                  <Text c="red" size="xs">
                    {t('sampling.notes.commentRequired')}
                  </Text>
                ) : null}

                <span className={classes.greenLine} />

                <div className={classes.mediaRow}>
                  {localImages.map((image) => (
                    <div className={classes.thumbWrap} key={image.id}>
                      <img alt={t('sampling.notes.newImageAlt')} className={classes.thumbImage} src={image.previewUrl} />
                      <ActionIcon
                        aria-label={t('sampling.notes.removePhotoAria')}
                        className={classes.thumbDelete}
                        onClick={() => handleRemoveLocalImage(image.id)}
                        radius="xl"
                        size={22}
                        variant="filled"
                      >
                        <XIcon size={12} />
                      </ActionIcon>
                    </div>
                  ))}

                  <button className={classes.addPhotoTile} onClick={handlePickImage} type="button">
                    <Plus size={22} />
                    <span>{t('sampling.notes.takePhoto')}</span>
                  </button>
                  <input
                    accept="image/*"
                    capture="environment"
                    onChange={handleAddImage}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    type="file"
                  />
                </div>
              </Stack>
            </Stack>
          </ScrollArea>

          <div className={classes.footerSticky}>
            <div className={classes.footerActions}>
              <Button className={classes.cancelButton} onClick={handleCancel} radius="lg">
                {t('common.cancel')}
              </Button>
              <Button
                className={classes.saveButton}
                disabled={!canSave}
                loading={createNoteMutation.isPending}
                onClick={() => {
                  setSubmitAttempted(true);
                  if (!canSave) return;
                  createNoteMutation.mutate();
                }}
                radius="lg"
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Stack>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default PointNoteCreatePage;
